import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  LinearProgress,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';

/**
 * Build manual test case payload for POST /automation/v1/manual-readiness.
 * Shared with the legacy automation upload dialog shape (file vs paste tabs).
 */
export function buildReadinessIntakePayload({
  uploadTab,
  uploadTitle,
  uploadFile,
  uploadText,
  fileText,
  requireTitle = false,
  titleFormat = 'readiness',
}) {
  if (requireTitle && !uploadTitle?.trim()) {
    return { error: 'Please enter a test case name' };
  }
  if (uploadTab === 0) {
    if (!uploadFile) {
      return { error: 'Please select a file' };
    }
    const content = fileText ?? '';
    if (!content.trim()) {
      return { error: 'Selected file is empty or could not be read' };
    }
    return {
      payload: {
        content,
        source: 'txt_upload',
        filename: uploadFile.name,
        uploadTab,
        uploadFile,
        uploadTitle,
        uploadText,
      },
    };
  }
  if (!uploadText?.trim()) {
    return { error: 'Please enter test case content' };
  }
  let content = uploadText.trim();
  if (uploadTitle?.trim()) {
    const title = uploadTitle.trim();
    if (titleFormat === 'legacy-markdown') {
      content = `# ${title}\n\n${content}`;
    } else if (!content.toLowerCase().startsWith('test case')) {
      content = `Test Case: ${title}\n${content}`;
    }
  }
  return {
    payload: {
      content,
      source: 'paste',
      filename: null,
      uploadTab,
      uploadFile,
      uploadTitle,
      uploadText,
    },
  };
}

/**
 * Reusable manual test case intake UI (file upload + paste tabs).
 * Used by Automation readiness (safe analyze flow) and legacy automation upload dialog.
 */
const AutomationManualIntake = ({
  busy = false,
  error = null,
  onSubmit,
  submitLabel = 'Analyze Automation Readiness',
  requireTitle = false,
  accept = '.txt,text/plain',
  supportedFormatsLabel = 'TXT',
  showTitleField = true,
  pasteTestId = 'readiness-input',
  fileInputTestId = 'readiness-file-input',
  analyzeTestId = 'readiness-analyze',
  titleFieldLabel = 'Test Case Name (optional)',
  titlePlaceholder = 'e.g., Import Single JIRA item',
  titleFormat = 'readiness',
  fileRequiresRead = true,
  pastePlaceholder = `Test Case 1: Import Single JIRA item
1. Login to the application
2. Navigate to Product Knowledge
3. Import story CAR-27 from Jira
Expected: CAR-27 row is visible in the table`,
}) => {
  const [uploadTab, setUploadTab] = useState(0);
  const [uploadTitle, setUploadTitle] = useState('');
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadText, setUploadText] = useState('');
  const [fileText, setFileText] = useState('');
  const [localError, setLocalError] = useState(null);

  const displayError = error || localError;

  const canSubmit =
    (!requireTitle || uploadTitle.trim().length > 0) &&
    (uploadTab === 0
      ? Boolean(uploadFile && (fileRequiresRead ? fileText.trim() : true))
      : uploadText.trim().length > 0);

  const handleFileChange = (event) => {
    const file = event.target.files?.[0];
    setLocalError(null);
    if (!file) {
      setUploadFile(null);
      setFileText('');
      return;
    }
    const lower = file.name.toLowerCase();
    const txtOnlyMode = accept === '.txt,text/plain';
    if (txtOnlyMode && !lower.endsWith('.txt')) {
      setLocalError('Only .txt files are supported for now.');
      setUploadFile(null);
      setFileText('');
      event.target.value = '';
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setUploadFile(file);
      setFileText(String(reader.result || ''));
    };
    reader.onerror = () => {
      setLocalError('Failed to read the file.');
      setUploadFile(null);
      setFileText('');
    };
    reader.readAsText(file);
    event.target.value = '';
  };

  const handleSubmit = () => {
    setLocalError(null);
    const built = buildReadinessIntakePayload({
      uploadTab,
      uploadTitle,
      uploadFile,
      uploadText,
      fileText,
      requireTitle,
      titleFormat,
    });
    if (built.error) {
      setLocalError(built.error);
      return;
    }
    onSubmit(built.payload);
  };

  return (
    <Stack spacing={2} data-testid="automation-manual-intake">
      {busy && <LinearProgress />}

      {showTitleField && (
        <TextField
          label={titleFieldLabel}
          value={uploadTitle}
          onChange={(e) => setUploadTitle(e.target.value)}
          placeholder={titlePlaceholder}
          fullWidth
          disabled={busy}
          required={requireTitle}
          inputProps={{ 'data-testid': 'intake-title' }}
        />
      )}

      <Tabs
        value={uploadTab}
        onChange={(_, value) => {
          setUploadTab(value);
          setLocalError(null);
        }}
        aria-label="manual-intake-tabs"
        sx={{
          '& .MuiTab-root': { fontWeight: 500, textTransform: 'none', fontSize: '0.95rem' },
          '& .Mui-selected': { fontWeight: 600, color: 'primary.main' },
        }}
      >
        <Tab label="Upload File" />
        <Tab label="Paste Text" />
      </Tabs>

      {uploadTab === 0 && (
        <Stack spacing={1.5}>
          <label htmlFor={fileInputTestId}>
            <input
              id={fileInputTestId}
              type="file"
              style={{ display: 'none' }}
              onChange={handleFileChange}
              disabled={busy}
              accept={accept}
              data-testid={fileInputTestId}
            />
            <Button variant="outlined" component="span" disabled={busy}>
              {uploadFile ? 'Change file' : 'Browse file'}
            </Button>
          </label>
          <Typography variant="body2" color="text.secondary">
            Supported: {supportedFormatsLabel}
          </Typography>
          {uploadFile && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                p: 1.5,
                bgcolor: 'action.hover',
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Typography variant="body2" sx={{ fontSize: '1.2rem' }}>📄</Typography>
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>{uploadFile.name}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {Math.round(uploadFile.size / 1024)} KB
                </Typography>
              </Box>
            </Box>
          )}
        </Stack>
      )}

      {uploadTab === 1 && (
        <TextField
          label="Manual test cases"
          value={uploadText}
          onChange={(e) => setUploadText(e.target.value)}
          placeholder={pastePlaceholder}
          minRows={8}
          maxRows={20}
          multiline
          fullWidth
          disabled={busy}
          inputProps={{ 'data-testid': pasteTestId }}
        />
      )}

      {displayError && (
        <Alert severity="error" data-testid="intake-error">
          {displayError}
        </Alert>
      )}

      <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
        <Button
          variant="contained"
          startIcon={busy ? <CircularProgress size={16} color="inherit" /> : <PlayArrowIcon />}
          onClick={handleSubmit}
          disabled={busy || !canSubmit}
          data-testid={analyzeTestId}
        >
          {busy ? 'Analyzing…' : submitLabel}
        </Button>
      </Box>
    </Stack>
  );
};

export default AutomationManualIntake;
